import { createStore } from '@shared/utils/state/state-factory'
import type { CountryCode } from 'libphonenumber-js'
import { REGIONS } from 'v2/configs/constants'
import type { RNumberSearch } from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'
import type { NumberSeries } from '../number-compliance/types'
import { getCountryFlowConfig } from './buy-number/country-configs'
import { type Capability, DEFAULT_SERVICES } from './buy-number/search-params'
import type { BuyNumberStep } from './types'

/**
 * Default number type is always 'all' regardless of country.
 */
const getDefaultNumberType = (_country: CountryCode): string => {
  return 'all'
}

const getDefaultCountry = (): CountryCode => {
  const { region } = getLocalAuth()
  if (region === REGIONS.IN) return 'IN'
  return 'US'
}

interface BuyNumberState {
  currentStep: BuyNumberStep
  selectedNumber: RNumberSearch | null
  selectedCompliance: string | null
  country: CountryCode
  numberType: string
  numberPrefix: string
  services: Capability[]
  haEnabled: boolean
  // True when buying back a previously unrented number (re-rent), so the buy
  // request bypasses blocked-inventory checks. Only the Unrented page sets it.
  isRerent: boolean
  // KYC step search term. Here rather than in the step, because the search input
  // renders from the sheet's sticky header (so it survives scrolling) while the
  // query that consumes it lives in the step body.
  complianceSearch: string
}

interface BuyNumberActions {
  setCurrentStep: (step: BuyNumberStep) => void
  setSelectedNumber: (number: RNumberSearch | null) => void
  // series (derived from the compliance alias) pins the number search to a
  // 140/160 series; pass 'other' to clear the pinned prefix, or omit/null when
  // the 140/160 flag is off to leave the user's manual prefix choice untouched.
  setSelectedCompliance: (compliance: string | null, series?: NumberSeries | null) => void
  setCountry: (country: CountryCode) => void
  setNumberType: (numberType: string) => void
  setNumberPrefix: (numberPrefix: string) => void
  setServices: (services: Capability[]) => void
  setHaEnabled: (haEnabled: boolean) => void
  setIsRerent: (isRerent: boolean) => void
  setComplianceSearch: (complianceSearch: string) => void
  reset: () => void
}

const getInitialState = (): BuyNumberState => {
  const country = getDefaultCountry()
  return {
    // First step depends on the country's flow (Compliance for IN, Select Number for US).
    currentStep: getCountryFlowConfig(country).stepSequence[0],
    selectedNumber: null,
    selectedCompliance: null,
    country,
    numberType: getDefaultNumberType(country),
    numberPrefix: '',
    services: [...DEFAULT_SERVICES],
    haEnabled: false,
    isRerent: false,
    complianceSearch: '',
  }
}

export const useBuyNumberStore = createStore<BuyNumberState & BuyNumberActions>(
  'buyNumber',
  (set) => ({
    ...getInitialState(),
    setCurrentStep: (step) => set({ currentStep: step }),
    setSelectedNumber: (number) => set({ selectedNumber: number }),
    setSelectedCompliance: (compliance, series) =>
      set((state) => {
        state.selectedCompliance = compliance
        // No series (140/160 flag off): leave the prefix and number untouched.
        if (!series) return
        // Map the currently-pinned prefix back to its series; a manual 080/022
        // prefix (or none) is the "other" landline series.
        const currentSeries =
          state.numberPrefix === '140' || state.numberPrefix === '160'
            ? state.numberPrefix
            : 'other'
        // Same series: keep the user's prefix and any number they already picked.
        if (series === currentSeries) return
        // Series changed: pin the new prefix ('' lets the user pick 080/022 for
        // the "other" flow) and drop a number chosen for the old series so it
        // can't be carried into Configuration.
        state.numberPrefix = series === 'other' ? '' : series
        state.selectedNumber = null
      }),
    setCountry: (country) => {
      const newConfig = getCountryFlowConfig(country)
      set({
        country,
        numberType: getDefaultNumberType(country),
        numberPrefix: '',
        services: [...DEFAULT_SERVICES],
        currentStep: newConfig.stepSequence[0], // Reset to first step of new flow
        selectedNumber: null,
        selectedCompliance: null,
        complianceSearch: '', // stale search terms from the old country's list must not carry over
        isRerent: false, // switching country starts a fresh (non-rerent) buy
      })
    },
    setNumberType: (numberType) => set({ numberType }),
    setNumberPrefix: (numberPrefix) => set({ numberPrefix }),
    setServices: (services) => set({ services }),
    setHaEnabled: (haEnabled) => set({ haEnabled }),
    setIsRerent: (isRerent) => set({ isRerent }),
    setComplianceSearch: (complianceSearch) => set({ complianceSearch }),
    reset: () => set({ ...getInitialState() }),
  }),
)
